import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { formatPayloadValue } from "../../../helpers/activityLogHelpers";
import ActivityLogTimelineView from "../../../components/ActivityLog/ActivityLogTimelineView";

const CreditLimitActivityLog = ({
  busEntId,
  requestId,
  entityId,
  entName,
  orgName,
  title,
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } = useSelector((state) => state?.activityLog) || {};
  const targetEntityId = requestId || entityId || busEntId || "ALL";
  const displayTitle = title || "Credit Limit Lifecycle History";

  useEffect(() => {
    dispatch(getActivityLog({ module: "CREDIT_LIMIT", entityId: targetEntityId, page, limit }));
  }, [dispatch, targetEntityId, page]);

  const getActionConfig = (action) => {
    switch (action) {
      case "CREDIT_LIMIT_CREATED": return { label: "Credit Limit Created", color: "#e6f7ff", textColor: "#1890ff" };
      case "CREDIT_LIMIT_REQUEST_RESUBMITTED": return { label: "Credit Limit Request Resubmitted", color: "#e6f7ff", textColor: "#1890ff" };
      case "CREDIT_LIMIT_APPROVED": return { label: "Credit Limit Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "CREDIT_LIMIT_REJECTED":
      case "BUS_ENT_REJECTED": return { label: "Credit Limit Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "CREDIT_LIMIT_ENHANCED": return { label: "Credit Limit Enhanced", color: "#e6f7ff", textColor: "#1890ff" };
      case "CREDIT_LIMIT_DEACTIVATED":
      case "BUSINESS_DEACTIVATED": return { label: "Credit Limit Deactivated", color: "#fff0f6", textColor: "#eb2f96" };
      case "CREDIT_LIMIT_REACTIVATED":
      case "BUS_ENT_ACTIVATED": return { label: "Credit Limit Reactivated", color: "#e6ffed", textColor: "#22c55e" };
      case "CREDIT_LIMIT_REOPENED":
      case "BUS_ENT_REOPENED": return { label: "Credit Limit Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      case "CREDIT_LIMIT_REQUEST_REOPENED": return { label: "Credit Limit Request Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      default: return { label: action ? action.replace(/_/g, " ") : "Activity", color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const bName = orgName || log.payload?.busName || log.payload?.orgName;
    const eName = entName || log.payload?.entName || log.payload?.enterpriseName;
    const reqId = log.payload?.requestId || (String(log.entityId || "").startsWith("CLR") ? log.entityId : null);
    const reqSuffix = reqId ? ` (${reqId})` : "";
    const targetName = bName && eName ? `"${bName} & ${eName}"` : (bName ? `"${bName}"` : (eName ? `"${eName}"` : "Credit Limit"));
    
    if (log.action === "CREDIT_LIMIT_CREATED") {
      return `<strong>${name}</strong> (${role}) <strong>CREATED</strong> credit limit request${reqSuffix} for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_REQUEST_RESUBMITTED") {
      return `<strong>${name}</strong> (${role}) <strong>RESUBMITTED</strong> credit limit request${reqSuffix} for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> credit limit${reqSuffix} for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_ENHANCED") {
      return `<strong>${name}</strong> (${role}) <strong>ENHANCED</strong> credit limit for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_DEACTIVATED" || log.action === "BUSINESS_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> credit limit for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_REACTIVATED" || log.action === "BUS_ENT_ACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> credit limit for <strong>${targetName}</strong>`;
    }
    if (log.action === "CREDIT_LIMIT_REJECTED" || log.action === "BUS_ENT_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> credit limit request${reqSuffix} for <strong>${targetName}</strong>`;
    }
    if (log.action === "BUS_ENT_REOPENED" || log.action === "CREDIT_LIMIT_REQUEST_REOPENED") {
      return `<strong>${name}</strong> (${role}) <strong>REOPENED</strong> credit limit request${reqSuffix} for <strong>${targetName}</strong>`;
    }
    
    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on <strong>${targetName}</strong>`;
  };

  return (
    <ActivityLogTimelineView
      title={displayTitle}
      totalRecords={totalRecords}
      loading={loading}
      logs={logs}
      page={page}
      setPage={setPage}
      limit={limit}
      getActionConfig={getActionConfig}
      formatDesc={formatDesc}
      filterPayloadEntries={(payload) =>
        payload && typeof payload === "object"
          ? Object.entries(payload).filter(
              ([key]) =>
                key !== "entityTypeId" &&
                key !== "entityType" &&
                key !== "businessTypeId" &&
                key !== "businessType"
            )
          : []
      }
      formatPayloadKey={(key) => key.replace(/([A-Z])/g, " $1").trim()}
      formatPayloadValue={formatPayloadValue}
    />
  );
};

export default CreditLimitActivityLog;
